<script setup lang="ts">
import type { GameShipCommercial } from "~~/types/gamedata";

const props = defineProps({
  file: {
    type: Object as () => GameShipCommercial,
    required: true,
  },
});

const runtimeConfig = useRuntimeConfig();

const videoSrc = computed(() => {
  const base = String(runtimeConfig.public.ASSETS_URL).replace(/\/$/, '');
  return `${base}/${props.file.id}`;
});

const playerRoot = ref<HTMLElement | null>(null);
const videoEl = ref<HTMLVideoElement | null>(null);
const progressTrack = ref<HTMLElement | null>(null);

const isPlaying = ref(false);
const isLoading = ref(true);
const isMuted = ref(false);
const volume = ref(1);
const currentTime = ref(0);
const duration = ref(0);
const isFullscreen = ref(false);
const showControls = ref(true);

let hideControlsTimeout: ReturnType<typeof setTimeout> | null = null;

function togglePlay() {
  if (!videoEl.value) return;
  if (videoEl.value.paused) videoEl.value.play();
  else videoEl.value.pause();
}

function onPlay() { isPlaying.value = true; }
function onPause() { isPlaying.value = false; }
function onWaiting() { isLoading.value = true; }
function onCanPlay() { isLoading.value = false; }
function onEnded() { isPlaying.value = false; showControls.value = true; }

function onTimeUpdate() {
  if (!videoEl.value) return;
  currentTime.value = videoEl.value.currentTime;
}

function onLoadedMetadata() {
  if (!videoEl.value) return;
  duration.value = videoEl.value.duration;
  volume.value = videoEl.value.volume;
  isLoading.value = false;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const progressPercent = computed(() => (duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0));

function seekFromEvent(event: PointerEvent) {
  if (!videoEl.value || !progressTrack.value || !duration.value) return;
  const rect = progressTrack.value.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  videoEl.value.currentTime = ratio * duration.value;
}

function scrub(event: PointerEvent) {
  seekFromEvent(event);
}

function endScrub() {
  window.removeEventListener('pointermove', scrub);
  window.removeEventListener('pointerup', endScrub);
}

function startScrub(event: PointerEvent) {
  seekFromEvent(event);
  window.addEventListener('pointermove', scrub);
  window.addEventListener('pointerup', endScrub);
}

function toggleMute() {
  if (!videoEl.value) return;
  videoEl.value.muted = !videoEl.value.muted;
  isMuted.value = videoEl.value.muted;
}

function onVolumeInput(event: Event) {
  if (!videoEl.value) return;
  const value = Number((event.target as HTMLInputElement).value);
  videoEl.value.volume = value;
  volume.value = value;
  videoEl.value.muted = value === 0;
  isMuted.value = value === 0;
}

const volumeIcon = computed(() => {
  if (isMuted.value || volume.value === 0) return 'i-lucide-volume-x';
  return volume.value < 0.5 ? 'i-lucide-volume-1' : 'i-lucide-volume-2';
});

async function toggleFullscreen() {
  if (!playerRoot.value) return;
  if (!document.fullscreenElement) await playerRoot.value.requestFullscreen();
  else await document.exitFullscreen();
}

function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === playerRoot.value;
}

function scheduleHideControls() {
  if (hideControlsTimeout) clearTimeout(hideControlsTimeout);
  hideControlsTimeout = setTimeout(() => {
    if (isPlaying.value) showControls.value = false;
  }, 2500);
}

function handlePointerMove() {
  showControls.value = true;
  scheduleHideControls();
}

onMounted(() => {
  document.addEventListener('fullscreenchange', onFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
  if (hideControlsTimeout) clearTimeout(hideControlsTimeout);
  window.removeEventListener('pointermove', scrub);
  window.removeEventListener('pointerup', endScrub);
});
</script>

<template>
  <div
    ref="playerRoot"
    class="group relative aspect-video w-full overflow-hidden rounded-lg border border-default bg-black"
    @pointermove="handlePointerMove"
    @mouseleave="isPlaying && (showControls = false)"
  >
    <video
      ref="videoEl"
      :src="videoSrc"
      class="size-full object-contain"
      playsinline
      preload="metadata"
      @click="togglePlay"
      @play="onPlay"
      @pause="onPause"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @waiting="onWaiting"
      @canplay="onCanPlay"
      @ended="onEnded"
    />

    <div
      class="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white backdrop-blur transition-opacity duration-300"
      :class="showControls ? 'opacity-100' : 'opacity-0'"
    >
      <UIcon name="i-lucide-clapperboard" class="size-3.5 text-aris-400" />
      {{ file.title || 'Commercial' }}
    </div>

    <div v-if="isLoading" class="pointer-events-none absolute inset-0 flex items-center justify-center">
      <UIcon name="i-lucide-loader-2" class="size-8 animate-spin text-aris-400" />
    </div>

    <button
      v-if="!isPlaying && !isLoading"
      type="button"
      class="absolute inset-0 flex items-center justify-center"
      aria-label="Abspielen"
      @click="togglePlay"
    >
      <span class="flex size-16 items-center justify-center rounded-full bg-aris-400/90 text-black shadow-[0_0_30px_rgba(45,212,191,0.45)] transition-transform duration-200 hover:scale-105">
        <UIcon name="i-lucide-play" class="size-7 translate-x-0.5" />
      </span>
    </button>

    <div
      class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-4 pb-3 pt-8 transition-opacity duration-300"
      :class="showControls ? 'opacity-100' : 'pointer-events-none opacity-0'"
    >
      <div
        ref="progressTrack"
        class="group/progress relative mb-2 h-1.5 w-full cursor-pointer rounded-full bg-white/20"
        @pointerdown="startScrub"
      >
        <div class="absolute inset-y-0 left-0 rounded-full bg-aris-400" :style="{ width: `${progressPercent}%` }" />
        <div
          class="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-aris-400 opacity-0 shadow transition-opacity duration-150 group-hover/progress:opacity-100"
          :style="{ left: `${progressPercent}%` }"
        />
      </div>

      <div class="flex items-center gap-3">
        <button type="button" class="text-white transition-colors hover:text-aris-400" :aria-label="isPlaying ? 'Pause' : 'Abspielen'" @click="togglePlay">
          <UIcon :name="isPlaying ? 'i-lucide-pause' : 'i-lucide-play'" class="size-5" />
        </button>

        <span class="font-mono text-xs tabular-nums text-white/80">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </span>

        <div class="ml-auto flex items-center gap-2">
          <button type="button" class="text-white transition-colors hover:text-aris-400" :aria-label="isMuted ? 'Ton an' : 'Stumm'" @click="toggleMute">
            <UIcon :name="volumeIcon" class="size-5" />
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            :value="isMuted ? 0 : volume"
            class="h-1 w-20 cursor-pointer accent-aris-400"
            @input="onVolumeInput"
          >

          <button type="button" class="text-white transition-colors hover:text-aris-400" aria-label="Vollbild" @click="toggleFullscreen">
            <UIcon :name="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'" class="size-5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
